
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import { useChangeManager } from '../context/ChangeManagerContext';
import dayjs from 'dayjs';
import {
  Box,
  Grid,
  CircularProgress,
  TextField,
  useMediaQuery,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import ReservationTable from '../components/ReservationTable';
import VehicleDetailPanel from '../components/VehicleDetailPanel';
import { updateVehicle } from '../api/vehicleService';
import { validateReservations } from '../schemas/dataSchemas';
import { useLineChat } from '../hooks/useLineChat';
import {
  fetchDispatchReservations,
  fetchStaffList,
  saveDispatchReservation,
  saveReservationStaff,
  toggleReservationLicense,
  updateReservationCommunicationTools,
  mergeStaffPriorities,
  onStaffPriorityChange,
} from '../api/dispatchService';
import { useSquarePaymentData } from '../hooks/useSquarePaymentData';
import { reservationSyncService } from '../services/reservationSyncService';
import { vehicleSyncService } from '../services/vehicleSyncService';
import { useReservationData } from '../context/ReservationDataContext';
import { isOnDuty } from '../utils/staffPriority';
import { SYNC_PAST_DAYS, SYNC_FUTURE_DAYS } from '../constants/calendar';

import { CAR_TYPES, ensureCarTypesLoaded } from '../data/carTypes';
import { useReservationStatuses } from '../hooks/useReservationStatuses';
import { useStatusMaster } from '../hooks/useStatusMaster';
import { useDateTags } from '../hooks/useDateTags';
import { useBottomTabs } from '../hooks/useBottomTabs';
import { useVehiclePanel } from '../hooks/useVehiclePanel';
import { useStatusDialogs } from '../hooks/useStatusDialogs';
import VehicleAssignModal from '../components/modals/VehicleAssignModal';
import LineChatPanel from '../components/LineChatPanel';
import VehiclePositionMap, { PICKUP_SUB_ZONES } from '../components/VehiclePositionMap';

const API_BASE = process.env.REACT_APP_API_URL || 'https://backend.n-car.info/api';

// ページタイトル設定（コンポーネント内で呼ばれる）
const PAGE_TITLE = '貸出返却リスト';

// Square Invoicesの対象ステータス（予約確定=1, 貸出済み=3, 貸出中=2, 要連絡/未確定=6）
const SQUARE_TARGET_STATUSES = [1, 2, 3, 4, 6];

// デフォルトのステータス（APIから取得できない場合のフォールバック）

const DEFAULT_RESERVATION_STATUS_LIST = [];

export default function DayListReservations() {
  const { token, user } = useAuth();
  const { recordChange } = useChangeManager();
  const isMobile = useMediaQuery('(max-width:800px)');
  const [tabValue, setTabValue] = useState(0);
  const [mobileSearchTerm, setMobileSearchTerm] = useState('');

  // LINE Chat integration for linking indicators
  const { users: lineUsers, fetchUsers: fetchLineUsers, sendMessage } = useLineChat();

  // ページタイトル設定
  useEffect(() => {
    document.title = PAGE_TITLE;
    fetchLineUsers(); // Initial fetch for LINE status indicators
  }, [fetchLineUsers]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const [hoveredVehicleId, setHoveredVehicleId] = useState(null);

  // LINE Chat modal state
  const [lineChatModal, setLineChatModal] = useState({ open: false, lineUser: null, reservationId: null });

  // Initialize date from URL query parameter 'date' or default to today
  const initialDate = searchParams.get('date') || dayjs().format('YYYY-MM-DD');
  const [selectedDate, setSelectedDate] = useState(initialDate);

  const { vehicles: contextVehicles, reservations: contextReservations } = useReservationData();

  const [departures, setDepartures] = useState([]);
  const [returns, setReturns] = useState([]);
  const [vehicles, setVehicles] = useState({});
  const [loading, setLoading] = useState(true);

  // 車両DnDスワップ
  const [vehicleDragSource, setVehicleDragSource] = useState(null);
  const [vehicleSwapConfirm, setVehicleSwapConfirm] = useState(null); // { source, target }

  // Context から vehicles を反映
  useEffect(() => {
    if (Object.keys(contextVehicles).length > 0) {
      setVehicles(contextVehicles);
    }
  }, [contextVehicles]);

  // Context の予約データ変更時に departures/returns を再フィルタ（Pusher 即時反映）
  useEffect(() => {
    if (contextReservations.length > 0 && selectedDate) {
      const filteredDep = contextReservations.filter(r => r.start_date && r.start_date.substring(0, 10) === selectedDate);
      filteredDep.sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
      const filteredRet = contextReservations.filter(r => r.end_date && r.end_date.substring(0, 10) === selectedDate);
      filteredRet.sort((a, b) => (a.end_date || '').localeCompare(b.end_date || ''));
      setDepartures(filteredDep);
      setReturns(filteredRet);
      setLoading(false);
      // 最終更新時刻を更新
      const now = new Date();
      setLastUpdated?.(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
    }
  }, [contextReservations, selectedDate]);

  const [refreshKey, setRefreshKey] = useState(0);
  // モバイル設定パネル用のカラム表示設定
  const MOBILE_FIELD_OPTIONS = [
    { id: 'name', label: '名前' },
    { id: 'vehicle', label: '車両' },
    { id: 'time', label: '時間' },
    { id: 'status', label: 'ステータス' },
    { id: 'pax', label: '人数' },
    { id: 'flight', label: 'ARR/DEP' },
    { id: 'license', label: '免許' },
    { id: 'payment', label: '支払' },
    { id: 'contact', label: '連絡手段' },
  ] as const;

  const [mobileFieldVisibility, setMobileFieldVisibility] = useState<Record<string, boolean>>(() => {
    const defaults = MOBILE_FIELD_OPTIONS.reduce((acc, f) => ({ ...acc, [f.id]: true }), {} as Record<string, boolean>);
    try {
      const saved = localStorage.getItem('reservation_table_visible_departure');
      if (saved) return { ...defaults, ...JSON.parse(saved) };
    } catch {}
    return defaults;
  });
  const [settingsVersion, setSettingsVersion] = useState(0);

  // モバイル設定パネル用の予約ステータス絞り込み
  const [mobileStatusFilter, setMobileStatusFilter] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('reservation_table_status_departure');
      if (saved) return JSON.parse(saved);
    } catch {}
    return {};
  });

  const handleMobileFieldToggle = (fieldId: string) => {
    setMobileFieldVisibility(prev => {
      const next = { ...prev, [fieldId]: !prev[fieldId] };
      // 両方のテーブル（departure/return）に同じ設定を適用
      localStorage.setItem('reservation_table_visible_departure', JSON.stringify(next));
      localStorage.setItem('reservation_table_visible_return', JSON.stringify(next));
      return next;
    });
    // ReservationTable を再マウントして設定を反映
    setSettingsVersion(v => v + 1);
  };

  const handleMobileStatusToggle = (statusId: string | number) => {
    setMobileStatusFilter(prev => {
      const currentState = prev[String(statusId)] !== false; // 未定義はデフォルト ON
      const next = { ...prev, [String(statusId)]: !currentState };
      // 両方のテーブル（departure/return）に同じ設定を適用
      localStorage.setItem('reservation_table_status_departure', JSON.stringify(next));
      localStorage.setItem('reservation_table_status_return', JSON.stringify(next));
      return next;
    });
    setSettingsVersion(v => v + 1);
  };

  const [compactMode, setCompactMode] = useState(() => {
    try {
      const raw = document.cookie.split('; ').find(c => c.startsWith('daylist_compact='));
      return raw ? raw.split('=')[1] === 'true' : false;
    } catch { return false; }
  });

  // ステータスマスター（共通フック経由）
  const { vehicleStatusList, locationStatusList, reservationStatusList } = useStatusMaster({ locationNullLabel: '-' });

  // ステータスモーダル・確認ダイアログ・トースト（フック経由）
  const {
    statusModal, setStatusModal, locationStatusModal, setLocationStatusModal, confirmDialog,
    toast, setToast, handleCloseToast,
    executeStatusUpdate,
    handleStatusClick, handleStatusUpdate, handleConfirmClose,
    handleLocationStatusClick, handleLocationStatusUpdate,
  } = useStatusDialogs({ user, vehicleStatusList, locationStatusList, setVehicles, setDepartures, setReturns }) as any;

  // 車両パネル（フック経由）
  const {
    selectedVehicle, setSelectedVehicle,
    selectedVehicleReservation, setSelectedVehicleReservation,
    vehiclePanelDirty, setVehiclePanelDirty,
    unsavedConfirm,
    handleBeforeOpenReservation, handleVehicleClick, handleClosePanel,
    handleUnsavedConfirmAction, handleSaveVehicle,
  } = useVehiclePanel({ user, setVehicles, onToast: setToast });
  const [upcomingStatuses, setUpcomingStatuses] = useState({}); // vehicle_id -> { type: 1..4, color: '...', label: '...' }
  const [contactModalReservation, setContactModalReservation] = useState<any>(null);
  const [allFutureReservations, setAllFutureReservations] = useState({});

  // LINEチャットモーダルを開くハンドラ
  const handleOpenLineChat = useCallback((lineUser, reservationId) => {
    setLineChatModal({ open: true, lineUser, reservationId: String(reservationId) });
  }, []);

  const handleCloseLineChat = useCallback(() => {
    setLineChatModal({ open: false, lineUser: null, reservationId: null });
    fetchLineUsers(); // チャット閉じた後に未読数等を更新
  }, [fetchLineUsers]);

  // 車両アサインモーダル用state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignTargetReservation, setAssignTargetReservation] = useState(null);
  const [carTypesList, setCarTypesList] = useState([]);

  // 車種ロード
  useEffect(() => {
    ensureCarTypesLoaded().then(() => {
      setCarTypesList([...CAR_TYPES.list]);
    });
  }, []);

  // スタッフ選択モーダル state
  const [staffList, setStaffList] = useState([]);
  const [staffModal, setStaffModal] = useState({
    open: false,
    reservation: null,
    staffType: null,  // 'driver' or 'handler'
    listType: null    // 'departure' or 'return'
  });

  // Square決済データ（DS1: search_by_contact.php 経由）
  const { squarePaymentCache, squarePaymentLoading, fetchPaymentData, getPaymentData, setSquarePaymentCache, refreshSingleContact } = useSquarePaymentData(SQUARE_TARGET_STATUSES);

  const [squareModal, setSquareModal] = useState<{
    open: boolean;
    reservation: any;
    paymentData: any;
    loading: boolean;
  }>({
    open: false,
    reservation: null,
    paymentData: null,
    loading: false,
  });

  // 下部タブ（フック経由）
  const {
    bottomTabValue, setBottomTabValue,
    bottomTabConfigs,
    tabConfigDialogOpen, setTabConfigDialogOpen,
    editingTabConfigs,
    locationGroupVehicles,
    handleBottomTabChange, handleOpenTabConfig, handleSaveTabConfig, handleTabConfigChange,
  } = useBottomTabs({ vehicles, locationStatusList, vehicleStatusList, reservationStatusList, departures, returns, onToast: setToast });

  // 日付タグ（フック経由）
  const {
    dateTags,
    displayDateTags,
    addDatePickerValue, setAddDatePickerValue,
    handleAddDateTag, handleRemoveDateTag, handleDateTagClick,
  } = useDateTags({ onToast: setToast, onDateSelect: setSelectedDate });

  // スタッフ一覧を取得
  useEffect(() => {
    const loadStaffList = async () => {
      try {
        const staff = await fetchStaffList({ per_page: 100 });
        setStaffList(mergeStaffPriorities(staff));
      } catch (error) {
        console.error('Failed to fetch staff list:', error);
      }
    };
    loadStaffList();
  }, []);

  // スタッフ優先度の変更を監視（本日勤務モーダルでの更新を即座に反映）
  useEffect(() => {
    const cleanup = onStaffPriorityChange(() => {
      // localStorageの最新priorityでstaffListを再マージ
      setStaffList(prev => mergeStaffPriorities(prev));
    });
    return cleanup;
  }, []);

  // ピン留めスタッフ一覧（運転者選択用）
  const pinnedStaffUsers = useMemo(() => {
    if (!Array.isArray(lineUsers)) return [];
    return lineUsers
      .filter(user => {
        if (!user.is_pinned) return false;
        const rids = user.reservation_ids;
        if (!rids) return false;
        const idArray = Array.isArray(rids) ? rids : String(rids).split(',').map(s => s.trim());
        return idArray.some(rid => String(rid).startsWith('STAFF:'));
      })
      .map(user => {
        const rids = user.reservation_ids;
        const idArray = Array.isArray(rids) ? rids : String(rids).split(',').map(s => s.trim());
        const staffId = idArray.find(id => String(id).startsWith('STAFF:'));
        const staffName = staffId?.replace('STAFF:', '') || user.line_display_name;
        return { ...user, staffName };
      });
  }, [lineUsers]);

  // Sync state with URL if URL changes (e.g. browser back button)
  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (dateParam && dateParam !== selectedDate) {
      setSelectedDate(dateParam);
    }
  }, [searchParams]);

  // selectedDate が SyncService のキャッシュ範囲内かチェック
  const isDateInSyncRange = useCallback((dateStr) => {
    const date = dayjs(dateStr);
    const today = dayjs();
    return date.isAfter(today.subtract(SYNC_PAST_DAYS + 1, 'day')) &&
      date.isBefore(today.add(SYNC_FUTURE_DAYS + 1, 'day'));
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;

      // キャッシュ範囲内であれば、reservationSyncService のキャッシュから絞り込む
      if (isDateInSyncRange(selectedDate)) {
        const cached = reservationSyncService.getReservations();
        if (cached.length > 0) {
          const filteredDep = cached.filter(r => r.start_date && r.start_date.substring(0, 10) === selectedDate);
          filteredDep.sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
          const filteredRet = cached.filter(r => r.end_date && r.end_date.substring(0, 10) === selectedDate);
          filteredRet.sort((a, b) => (a.end_date || '').localeCompare(b.end_date || ''));
          setDepartures(filteredDep);
          setReturns(filteredRet);
          setLoading(false);
          fetchLineUsers();
          return;
        }
      }

      setLoading(true);
      try {
        // Fetch Rentals (Departures) (rental_date_from/to = selectedDate)
        // Use service to ensure mappers (like json_data parsing) are applied
        const rawDepData = await fetchDispatchReservations({
          rentalDateFrom: selectedDate,
          rentalDateTo: selectedDate,
          start_date: '2000-01-01',
          end_date: '2100-12-31'
        });
        const depData = validateReservations(rawDepData);

        // Client-side filter fallback in case API ignores rental_date params
        // Use substring to avoid timezone issues (API returns ISO with offset, we just want the date part)
        const filteredDep = Array.isArray(depData)
          ? depData.filter(r => r.start_date && r.start_date.substring(0, 10) === selectedDate)
          : [];
        filteredDep.sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));

        setDepartures(filteredDep);

        // Fetch Returns (return_date_from/to = selectedDate)
        const rawRetData = await fetchDispatchReservations({
          returnDateFrom: selectedDate,
          returnDateTo: selectedDate,
          start_date: '2000-01-01',
          end_date: '2100-12-31'
        });
        const retData = validateReservations(rawRetData);

        // Client-side filter fallback
        const filteredRet = Array.isArray(retData)
          ? retData.filter(r => r.end_date && r.end_date.substring(0, 10) === selectedDate)
          : [];

        // 時間の昇順で並び替え
        filteredRet.sort((a, b) => {
          const timeA = a.end_date || '';
          const timeB = b.end_date || '';
          return timeA.localeCompare(timeB);
        });

        setReturns(filteredRet);

        // Square決済データを非同期で取得（UIブロックしない、確認済みはスキップ）
        fetchPaymentData([...filteredDep, ...filteredRet]);

      } catch (error) {
        console.error('Failed to fetch day list data', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    fetchLineUsers(); // Also refresh LINE users
  }, [token, selectedDate, refreshKey, fetchLineUsers]);

  // 同期サービスによる自動更新
  useEffect(() => {
    const handleReservationChanges = (changes, allReservations) => {
      // 選択中の日付に該当する予約を抽出
      const filteredDep = allReservations.filter(r => r.start_date && r.start_date.substring(0, 10) === selectedDate);
      filteredDep.sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
      const filteredRet = allReservations.filter(r => r.end_date && r.end_date.substring(0, 10) === selectedDate);
      filteredRet.sort((a, b) => (a.end_date || '').localeCompare(b.end_date || ''));

      setDepartures(filteredDep);
      setReturns(filteredRet);
      console.log(`[DayList] Sync updated: deps=${filteredDep.length}, rets=${filteredRet.length} (Total in sync cache: ${allReservations.length})`);
    };

    const unsubscribeRes = reservationSyncService.subscribe(handleReservationChanges);

    // 購読開始時に現在のキャッシュからも1回更新（同期を待たずに済むように）
    const currentRes = reservationSyncService.getReservations();
    if (currentRes.length > 0) {
      handleReservationChanges([], currentRes);
    }

    return () => {
      unsubscribeRes();
    };
  }, [selectedDate]);

  // 本日以降の予約状況を非同期で取得して「i」アイコンのステータスと車両位置の次回予約を設定
  useEffect(() => {
    const fetchUpcomingReservations = async () => {
      try {
        const baseDate = dayjs(selectedDate);
        const startDate = baseDate.format('YYYY-MM-DD');
        // 本日以降30日先まで取得し、次回予約を正確に表示する
        const endDate = baseDate.add(30, 'day').format('YYYY-MM-DD');

        // 30日以内の予約を全取得
        const rawData = await fetchDispatchReservations({
          start_date: `${startDate} 00:00:00`,
          end_date: `${endDate} 23:59:59`
        });
        const data = validateReservations(rawData);

        // 車両ごとの状況を解析
        const statusMap = {};
        const futureResMap = {};
        const date1Str = baseDate.add(1, 'day').format('YYYY-MM-DD');
        const date2Str = baseDate.add(2, 'day').format('YYYY-MM-DD');

        // 除外するステータス（オファー中、予約がいっぱい、予約に至らなかった、キャンセル済み）
        const excludeStatuses = ['cancelled', 'nonreserved', 'full', 'offer', 4, 5, 6, 7];

        // すべての車両について次回の予約を調査
        Object.values(vehicles).forEach((v: any) => {
          const vehicleId = v.id;
          if (!vehicleId) return;

          // この車両の本日以降の予約を抽出（本日当日分も含む）
          const futureRes = data.filter(r =>
            Number(r.vehicle_id) === Number(vehicleId) &&
            !dayjs(r.start_date).isBefore(baseDate.startOf('day')) &&
            !excludeStatuses.includes(r.status) &&
            !excludeStatuses.includes(Number(r.status))
          );

          if (futureRes.length === 0) {
            statusMap[vehicleId] = { type: 4, color: '#bdbdbd', label: '次回予約なし', next: null };
            return;
          }

          // 開始時間で昇順ソート
          futureRes.sort((a, b) => dayjs(a.start_date).unix() - dayjs(b.start_date).unix());

          const next = futureRes[0];
          const nextStartDate = dayjs(next.start_date).format('YYYY-MM-DD');

          let type = 4;
          let color = '#bdbdbd';
          let label = '次回予約なし';

          if (nextStartDate === startDate) {
            type = 1;
            color = '#d32f2f';
            label = `次${dayjs(next.start_date).format('HH:mm')}に出る`;
          } else if (nextStartDate === date1Str) {
            type = 2;
            color = '#1976d2';
            label = '翌日に予約あり';
          } else if (nextStartDate === date2Str) {
            type = 3;
            color = '#03a9f4';
            label = '翌々日に予約あり';
          } else {
            // 3日以上先の予約がある場合
            type = 5;
            color = '#9e9e9e';
            label = '3日以上先に予約あり';
          }

          statusMap[vehicleId] = {
            type,
            color,
            label,
            next: {
              id: next.id,
              start_date: next.start_date,
              customer_name: next.customer_name
            }
          };
          futureResMap[vehicleId] = futureRes;
        });

        setUpcomingStatuses(statusMap);
        setAllFutureReservations(futureResMap);
      } catch (err) {
        console.error('Failed to fetch upcoming reservations:', err);
      }
    };

    fetchUpcomingReservations();
  }, [selectedDate, returns, vehicles]);

  // 貸出リストの各行に対して、同日のそれ以前に返却があったか判定
  const finalDepartures = useMemo(() => {
    return departures.map(d => {
      // 当日、この出発より前に返却があるか
      const hasEarlierReturn = returns.some(r =>
        String(r.vehicle_id) === String(d.vehicle_id) &&
        dayjs(r.end_date).isBefore(dayjs(d.start_date))
      );
      return { ...d, isTurnaroundDeparture: hasEarlierReturn };
    });
  }, [departures, returns]);

  // 返却リストの各行に対して、それ以降に予約があるか判定（本日、明日、明後日）
  const finalReturns = useMemo(() => {
    const baseDate = dayjs(selectedDate);
    const startDate = baseDate.format('YYYY-MM-DD');
    const date1Str = baseDate.add(1, 'day').format('YYYY-MM-DD');
    const date2Str = baseDate.add(2, 'day').format('YYYY-MM-DD');

    return returns.map(r => {
      const vId = r.vehicle_id;
      const vehicleFutureRes = allFutureReservations[vId] || [];

      // この返却より後の最も早い出発を探す
      const next = vehicleFutureRes.find(res =>
        dayjs(res.start_date).isAfter(dayjs(r.end_date))
      );

      let rowAttention = null;
      if (next) {
        const nextStartDate = dayjs(next.start_date).format('YYYY-MM-DD');
        if (nextStartDate === startDate) {
          rowAttention = { color: '#d32f2f', label: `次${dayjs(next.start_date).format('HH:mm')}に出る` };
        } else if (nextStartDate === date1Str) {
          rowAttention = { color: '#1976d2', label: '翌日に予約あり' };
        } else if (nextStartDate === date2Str) {
          rowAttention = { color: '#03a9f4', label: '翌々日に予約あり' };
        }
      }
      return { ...r, rowAttention };
    });
  }, [returns, allFutureReservations, selectedDate]);

  const formatTime = (isoString) => {
    if (!isoString) return '';
    return dayjs(isoString).format('HH:mm');
  };

  // 車両チェック項目更新ハンドラー
  const handleCheckUpdate = async (vehicle, checkItems) => {
    if (!vehicle || !vehicle.id) return;

    // Optimistic update
    const updatedVehicle = {
      ...vehicle,
      check_situations: checkItems,
    };
    setVehicles(prev => ({ ...prev, [vehicle.id]: updatedVehicle }));

    try {
      const result = await updateVehicle({
        id: vehicle.id,
        check_situations: checkItems.map(item => ({
          id: item.id,
          value: item.value,
        })),
      }, user?.id);

      if (result) {
        setVehicles(prev => ({ ...prev, [result.id]: result }));
        setToast({ open: true, message: 'チェック項目を更新しました', severity: 'success' });
      }
    } catch (error) {
      console.error('Failed to update check items:', error);
      setToast({ open: true, message: 'チェック項目の更新に失敗しました', severity: 'error' });
      // Revert
      setVehicles(prev => ({ ...prev, [vehicle.id]: vehicle }));
    }
  };

  // 現在のリストタイプに応じた位置ステータス選択肢を取得
  const getLocationStatusOptions = (listType) => {
    // showInフィールドは削除されたため、すべての位置ステータスを返す
    // locationStatusList は useMemo で常に配列を返すため、フォールバック不要
    return locationStatusList;
  };

  // スタッフ選択クリックハンドラー
  const handleStaffClick = (reservation, staffType, listType) => {
    setStaffModal({
      open: true,
      reservation,
      staffType,  // 'driver' or 'handler'
      listType    // 'departure' or 'return'
    });
  };

  // スタッフ選択ハンドラー
  const handleStaffSelect = async (staff) => {
    const { reservation, staffType, listType } = staffModal;
    if (!reservation) return;

    setStaffModal({ open: false, reservation: null, staffType: null, listType: null });

    // ピン留めスタッフかどうか判定
    const isPinnedStaff = staff && staff._isPinnedStaff;

    // スタッフフィールドを決定
    // 貸出(departure): driver -> pu_staff_id, handler -> rent_staff_id
    // 返却(return): driver -> do_staff_id, handler -> return_staff_id
    let staffField;
    if (listType === 'departure') {
      staffField = staffType === 'driver' ? 'pu_staff_id' : 'rent_staff_id';
    } else {
      staffField = staffType === 'driver' ? 'do_staff_id' : 'return_staff_id';
    }

    // ピン留めスタッフの場合はID:14で登録、表示名はスタッフ登録名
    const staffId = staff ? (isPinnedStaff ? 14 : staff.id) : null;
    const displayName = staff ? (isPinnedStaff ? staff.staffName : staff.staff_name) : null;

    console.log('[DayList] handleStaffSelect called:', {
      reservationId: reservation.id,
      customerName: reservation.customer_name,
      staffType,
      listType,
      isPinnedStaff,
      staffId,
      displayName,
      staffField,
      originalStaff: staff,
    });

    // 表示用のフィールド名を決定
    const displayField = staffField.replace('_id', '');

    // Optimistic update
    const updatedReservation = {
      ...reservation,
      [displayField]: staff ? { id: staffId, name: displayName, staff_name: displayName, active: staff.active ?? 1, active_name: staff.active_name ?? '在籍', is_deleted: staff.is_deleted ?? false } : null
    };

    console.log('[DayList] Optimistic update:', {
      displayField,
      updatedValue: updatedReservation[displayField],
      fullField: `${displayField}.id=${staffId}, name=${displayName}`,
    });

    const updateRow = (r) => {
      if (String(r.id) !== String(reservation.id)) return r;
      return updatedReservation;
    };

    if (listType === 'departure') {
      setDepartures(prev => prev.map(updateRow));
    } else {
      setReturns(prev => prev.map(updateRow));
    }

    try {
      // reservationオブジェクト全体を渡す（APIに必須フィールドを含めるため）
      await saveReservationStaff(reservation, staffField, staffId);
      console.log('[DayList] saveReservationStaff success:', { staffField, staffId });
      const staffLabel = staffType === 'driver' ? '運転者' : '対応者';
      setToast({
        open: true,
        message: staff ? `${staffLabel}を${displayName}に設定しました` : `${staffLabel}を解除しました`,
        severity: 'success'
      });

      // ピン留めスタッフの場合: メモに記録 + LINEメッセージ送信
      if (isPinnedStaff) {
        const timestamp = dayjs().format('YYYY-MM-DD HH:mm');
        const staffLabel2 = listType === 'departure' ? '貸出運転者' : '返却運転者';
        const memoEntry = `---${staffLabel2}アサイン ${timestamp}---\n${staff.staffName}\n---`;
        const currentMemo = reservation.detail?.memo || reservation.memo || '';
        const updatedMemo = currentMemo ? `${currentMemo}\n${memoEntry}` : memoEntry;

        // メモを保存
        // 重要: reservation 全体を spread して既存の detail / car_type / vehicle_id 等を保持する
        // （bug fix: 過去に detail なしで save していたため、base_price / sum_price / car_type が
        //  バックエンドで 0 / '' にリセットされる事象が発生していた）
        try {
          await saveDispatchReservation({
            ...reservation,
            customer_name: reservation.customer_name || '予約',
            detail: {
              ...(reservation.detail || {}),
              memo: updatedMemo,
            },
          });
        } catch (e) {
          console.error('Failed to save memo for pinned staff:', e);
        }

        // LINEメッセージ送信
        const vehicle = vehicles[String(reservation.vehicle_id)];
        const vehicleCode = vehicle?.vehicle_code || reservation.vehicle_id || '未割当';
        const typeLabel = listType === 'departure' ? '[🚙 ピックアップ]' : '[🚗 ドロップオフ]';
        const lineMessage = `${typeLabel}\n車両: ${vehicleCode}\n顧客名: ${reservation.customer_name || ''}`;
        try {
          await sendMessage(staff.line_user_id, lineMessage);
        } catch (e) {
          console.error('Failed to send LINE message to pinned staff:', e);
        }
      }
    } catch (error) {
      console.error('Failed to update staff:', error);
      setToast({ open: true, message: 'スタッフの更新に失敗しました', severity: 'error' });
      // Revert
      if (listType === 'departure') {
        setDepartures(prev => prev.map(r => String(r.id) === String(reservation.id) ? reservation : r));
      } else {
        setReturns(prev => prev.map(r => String(r.id) === String(reservation.id) ? reservation : r));
      }
    }
  };

  // 免許確認トグルハンドラ
  const handleLicenseToggle = async (reservation, listType) => {
    // 現在の免許確認状態を取得（detailオブジェクト内）
    const currentLicense = Number(reservation.detail?.license ?? 0);
    const newLicense = currentLicense === 1 ? 0 : 1;

    // Optimistic update
    const updatedReservation = {
      ...reservation,
      detail: {
        ...reservation.detail,
        license: newLicense,
      }
    };

    const updateRow = (r) => {
      if (String(r.id) !== String(reservation.id)) return r;
      return updatedReservation;
    };

    if (listType === 'departure') {
      setDepartures(prev => prev.map(updateRow));
    } else {
      setReturns(prev => prev.map(updateRow));
    }

    try {
      await toggleReservationLicense(reservation, newLicense);
      setToast({
        open: true,
        message: newLicense === 1 ? '免許確認済みに更新しました' : '免許未確認に更新しました',
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to toggle license:', error);
      setToast({ open: true, message: '免許確認の更新に失敗しました', severity: 'error' });
      // Revert
      if (listType === 'departure') {
        setDepartures(prev => prev.map(r => String(r.id) === String(reservation.id) ? reservation : r));
      } else {
        setReturns(prev => prev.map(r => String(r.id) === String(reservation.id) ? reservation : r));
      }
    }
  };

  // 連絡手段更新ハンドラ
  const handleCommunicationToolsUpdate = async (reservation, tools, listType) => {
    // Optimistic update
    const updatedReservation = {
      ...reservation,
      detail: {
        ...reservation.detail,
        communication_tools: tools,
      }
    };

    const updateRow = (r) => {
      if (String(r.id) !== String(reservation.id)) return r;
      return updatedReservation;
    };

    if (listType === 'departure') {
      setDepartures(prev => prev.map(updateRow));
    } else {
      setReturns(prev => prev.map(updateRow));
    }

    try {
      await updateReservationCommunicationTools(reservation, tools);
      setToast({
        open: true,
        message: '連絡手段を更新しました',
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to update communication tools:', error);
      setToast({ open: true, message: '連絡手段の更新に失敗しました', severity: 'error' });
      // Revert
      if (listType === 'departure') {
        setDepartures(prev => prev.map(r => String(r.id) === String(reservation.id) ? reservation : r));
      } else {
        setReturns(prev => prev.map(r => String(r.id) === String(reservation.id) ? reservation : r));
      }
    }
  };

  const isActive = (r) => {
    const s = String(r.status);
    return s !== '4' && s !== '5' && s !== 'cancelled' && s !== 'nonreserved' && s !== 'full' && s !== 'offer';
  };

  // 車両ステータスを更新
  const handleUpdateVehicleStatus = useCallback(async (vehicleId, statusId) => {
    if (!vehicleId) return;
    try {
      const statusInfo = vehicleStatusList.find(s => s.id === statusId);
      await updateVehicle({
        id: vehicleId,
        status: statusId,
      }, user?.id);

      // ローカル状態を更新
      setVehicles(prev => ({
        ...prev,
        [String(vehicleId)]: {
          ...prev[String(vehicleId)],
          status: statusId,
          status_name: statusInfo?.label || '',
        },
      }));
      setToast({ open: true, message: '車両ステータスを更新しました', severity: 'success' });
    } catch (err) {
      console.error('Failed to update vehicle status:', err);
      setToast({ open: true, message: 'ステータスの更新に失敗しました', severity: 'error' });
      throw err; // Re-throw to allow modal to handle error if needed
    }
  }, [user?.id, vehicleStatusList]);

  // 車両アサインモーダルを開く
  const handleOpenAssignModal = useCallback((reservation) => {
    setAssignTargetReservation(reservation);
    setAssignModalOpen(true);
  }, []);

  // 車両アサインモーダルを閉じる
  const handleCloseAssignModal = useCallback(() => {
    setAssignModalOpen(false);
    setAssignTargetReservation(null);
  }, []);

  // 車両アサイン実行
  const executeAssignment = useCallback(async (reservation, vehicleId) => {
    try {
      // アサインする車両情報を取得
      const assignedVehicle = vehicles[String(vehicleId)];
      const updatePayload = {
        ...reservation,
        vehicle_id: vehicleId,
      };

      // 車両のアサイン時に車種を変更しないようにするため、
      // updatePayload.car_type の自動更新を削除しました。

      const savedReservation = await saveDispatchReservation(updatePayload);

      // ローカルステートを更新
      const updateRow = r => {
        if (String(r.id) === String(reservation.id)) {
          const updated = { ...savedReservation };
          if (updated.vehicle_id && vehicles[String(updated.vehicle_id)]) {
            updated.vehicle_code = vehicles[String(updated.vehicle_id)].vehicle_code;
          }
          return updated;
        }
        return r;
      };

      setDepartures(prev => prev.map(updateRow));
      setReturns(prev => prev.map(updateRow));

      handleCloseAssignModal();
      setToast({ open: true, message: '車両をアサインしました', severity: 'success' });
    } catch (err) {
      console.error('Failed to assign vehicle:', err);
      setToast({ open: true, message: '車両のアサインに失敗しました', severity: 'error' });
    }
  }, [vehicles, handleCloseAssignModal]);

  // 支払確認ボタンクリックハンドラ（DS1をメインデータソースとして使用）
  const handlePaymentCheck = useCallback(async (reservation) => {
    const contactCode = reservation.contact_code || reservation.detail?.contact_id;
    if (!contactCode) {
      setToast({ open: true, message: '予約番号が見つかりません', severity: 'warning' });
      return;
    }

    // キャッシュからデータを取得
    const cached = getPaymentData(contactCode);

    // ダイアログを表示
    setSquareModal({
      open: true,
      reservation,
      paymentData: cached || null,
      loading: !cached,
    });

    // キャッシュがない場合はDS1から取得
    if (!cached) {
      try {
        await refreshSingleContact(contactCode);
      } catch (error) {
        console.error('Failed to fetch payment data on demand:', error);
      }
    }
  }, [getPaymentData, refreshSingleContact]);

  // squarePaymentCache が更新されたらモーダルのデータも同期
  useEffect(() => {
    if (!squareModal.open || !squareModal.reservation) return;
    const contactCode = squareModal.reservation.contact_code || squareModal.reservation.detail?.contact_id;
    if (!contactCode) return;
    const entry = squarePaymentCache[contactCode];
    if (entry) {
      setSquareModal(prev => ({ ...prev, paymentData: entry, loading: false }));
    }
  }, [squarePaymentCache, squareModal.open, squareModal.reservation]);

  // スタッフの表示用フィルタリング（在籍中、または現在割り当て済みのスタッフ）
  const getFilteredStaffList = (_currentStaffId) => {
    if (!Array.isArray(staffList)) return [];
    return staffList
      .filter(staff => {
        // ID:1（APIテスト用スタッフ）は常に非表示
        if (staff.id === 1) return false;
        // is_active は NULL/undefined のスタッフもあるため、明示的な 0 のみ除外
        return staff.is_active !== 0 && !staff.is_deleted && isOnDuty(staff.priority);
      })
      .sort((a, b) => (b.priority || 0) - (a.priority || 0));
  };

  // 現在の予約に割り当てられているスタッフIDを取得
  const getCurrentStaffId = () => {
    const { reservation, staffType, listType } = staffModal;
    if (!reservation) return null;

    let staffObj;
    if (listType === 'departure') {
      staffObj = staffType === 'driver' ? reservation.pu_staff : reservation.rent_staff;
    } else if (listType === 'return') {
      staffObj = staffType === 'driver' ? reservation.do_staff : reservation.return_staff;
    }
    return staffObj?.id || null;
  };

  // 予約保存ハンドラー
  const handleReservationSave = async (reservation) => {
    try {
      const savedReservation = await saveDispatchReservation(reservation);
      setToast({ open: true, message: '予約を保存しました', severity: 'success' });

      // ローカルステートを直接更新
      // 編集データからdetailオブジェクトを構築
      const updatedDetail = {
        pax_children: reservation.pax_children,
        child_seat: reservation.child_seat,
        booster_seat: reservation.booster_seat,
        arrival_flight: reservation.arrival_flight,
        departure_flight: reservation.departure_flight,
        comment: reservation.comment,
        memo: reservation.memo,
        options: reservation.options,
        bk: reservation.bk,
        contact_plan: reservation.contact_plan,
        communication_tools: reservation.communication_tools,
      };

      // reservation.idは文字列の場合があるので、比較時に型を揃える
      const reservationId = reservation.id ? String(reservation.id) : null;
      const isNew = !reservationId || reservationId.startsWith('tmp_');

      if (isNew) {
        // 新規作成（コピー含む）の場合
        const newRow = {
          ...reservation,
          // NOTE: Fallback required for spread - savedReservation may be null/undefined
          ...(savedReservation || {}),
          // NOTE: Fallback required for spread. Detail is validated non-null by SyncService, but kept for runtime safety.
          detail: { ...(reservation.detail || {}), ...updatedDetail }
        };

        // 貸出リストに追加（日付が一致する場合）
        if (newRow.start_date && newRow.start_date.substring(0, 10) === selectedDate) {
          setDepartures(prev => [newRow, ...prev]);
        }
        // 返却リストに追加（日付が一致する場合）
        if (newRow.end_date && newRow.end_date.substring(0, 10) === selectedDate) {
          setReturns(prev => [newRow, ...prev]);
        }
      } else {
        // 既存の更新
        const updateRow = (r) => {
          if (String(r.id) !== reservationId) return r;
          return {
            ...r,
            // NOTE: Fallback required for spread - savedReservation may be null/undefined
            ...(savedReservation || {}),
            pax: reservation.pax,
            customer_name: reservation.customer_name || r.customer_name,
            // Update confirmation flags at top level as well to ensure display updates
            license: reservation.license,
            payment: reservation.payment,
            agreement: reservation.agreement,
            terms_and_conditions: reservation.terms_and_conditions,
            disclaimer: reservation.disclaimer,
            short_memo: reservation.short_memo,
            request_coming_time: reservation.request_coming_time,
            request_returning_time: reservation.request_returning_time,

            // NOTE: Fallback required for spread. Detail is validated non-null by SyncService, but kept for runtime safety.
            detail: { ...(r.detail || {}), ...updatedDetail }
          };
        };

        // departures（貸出）リストを更新
        setDepartures(prev => prev.map(updateRow));
        // returns（返却）リストを更新
        setReturns(prev => prev.map(updateRow));
      }

    } catch (error) {
      console.error('Failed to save reservation:', error);
      setToast({ open: true, message: '予約の保存に失敗しました', severity: 'error' });
      throw error;
    }
  };

  // 簡易更新ハンドラー（short_memo, request_time等）
  const handleQuickUpdate = async (reservation, updates) => {
    try {
      // 既存の予約情報にupdatesをマージ
      // updatesは { short_memo: '...' } や { request_coming_time: '...' } など
      const payload = {
        ...reservation,
        ...updates,
        // detailなどはそのまま維持するか、サーバー側でマージされることを期待
        // saveDispatchReservationの実装依存だが、基本はフルオブジェクト送信か
        // ここではreservationをベースにupdatesを上書きして送信
      };

      const savedReservation = await saveDispatchReservation(payload);
      setToast({ open: true, message: '更新しました', severity: 'success' });

      const updateRow = (r) => {
        if (String(r.id) !== String(reservation.id)) return r;
        return {
          ...r,
          // NOTE: Fallback required for spread - savedReservation may be null/undefined
          ...(savedReservation || {}),
          // 念のためupdatesの内容も反映（レスポンスに含まれない場合への保険）
          ...updates
        };
      };

      setDepartures(prev => prev.map(updateRow));
      setReturns(prev => prev.map(updateRow));
    } catch (error) {
      console.error('Failed to quick update:', error);
      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
    }
  };

  // 車両DnDスワップ: ドロップ時に確認Dialogを表示
  const handleVehicleSwapRequest = useCallback((sourceRow, targetRow) => {
    const srcVid = sourceRow.vehicle_id;
    const tgtVid = targetRow.vehicle_id;
    if (!srcVid || !tgtVid || String(srcVid) === String(tgtVid)) return;
    setVehicleSwapConfirm({ source: sourceRow, target: targetRow });
  }, []);

  // 車両DnDスワップ: 確認後に実行
  const executeVehicleSwap = useCallback(async () => {
    if (!vehicleSwapConfirm) return;
    const { source, target } = vehicleSwapConfirm;
    const srcVid = source.vehicle_id;
    const tgtVid = target.vehicle_id;
    setVehicleSwapConfirm(null);

    // 楽観的更新
    const swapRow = (r) => {
      if (String(r.id) === String(source.id)) return { ...r, vehicle_id: tgtVid };
      if (String(r.id) === String(target.id)) return { ...r, vehicle_id: srcVid };
      return r;
    };
    setDepartures(prev => prev.map(swapRow));
    setReturns(prev => prev.map(swapRow));

    try {
      await Promise.all([
        saveDispatchReservation({ ...source, vehicle_id: tgtVid }),
        saveDispatchReservation({ ...target, vehicle_id: srcVid }),
      ]);
      setToast({ open: true, message: '車両を入替しました', severity: 'success' });
    } catch (error) {
      console.error('Vehicle swap failed:', error);
      const rollback = (r) => {
        if (String(r.id) === String(source.id)) return { ...r, vehicle_id: srcVid };
        if (String(r.id) === String(target.id)) return { ...r, vehicle_id: tgtVid };
        return r;
      };
      setDepartures(prev => prev.map(rollback));
      setReturns(prev => prev.map(rollback));
      setToast({ open: true, message: '車両の入替に失敗しました', severity: 'error' });
    }
  }, [vehicleSwapConfirm]);

  // 最終更新時刻
  const [lastUpdated, setLastUpdated] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });

  // 手動更新ハンドラー
  const handleManualRefresh = useCallback(() => {
    reservationSyncService.forceSync();
    vehicleSyncService.forceSync();
    setRefreshKey(prev => prev + 1);
    const now = new Date();
    setLastUpdated(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
    setToast({ open: true, message: '最新データに更新しました', severity: 'success' });
  }, []);

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1, md: 2 }, position: 'relative' }}>
      {/* 日付選択 + タブ（スマホ時）を1つの sticky ブロックにまとめる */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        bgcolor: 'background.paper',
        mx: { xs: -1, md: -2 },
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'space-between', sm: 'center' },
          gap: 1,
          py: 1,
          px: { xs: 1, md: 2 },
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
          {/* 日付選択 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const today = dayjs().format('YYYY-MM-DD');
                setSelectedDate(today);
                setSearchParams({ date: today });
              }}
              sx={{
                minWidth: 'auto',
                px: 1,
                py: 0.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              本日
            </Button>
            <IconButton
              size="small"
              onClick={() => {
                const prev = dayjs(selectedDate).subtract(1, 'day').format('YYYY-MM-DD');
                setSelectedDate(prev);
                setSearchParams({ date: prev });
              }}
              sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}
            >
              <Typography sx={{ fontSize: 16, fontWeight: 'bold' }}>&lt;</Typography>
            </IconButton>
            <TextField
              type="date"
              value={selectedDate}
              onChange={(e) => {
                const val = e.target.value;
                if (val) {
                  setSelectedDate(val);
                  setSearchParams({ date: val });
                }
              }}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ width: 180 }}
            />
            <IconButton
              size="small"
              onClick={() => {
                const next = dayjs(selectedDate).add(1, 'day').format('YYYY-MM-DD');
                setSelectedDate(next);
                setSearchParams({ date: next });
              }}
              sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}
            >
              <Typography sx={{ fontSize: 16, fontWeight: 'bold' }}>&gt;</Typography>
            </IconButton>
          </Box>

          {/* コンパクト + 更新ボタン（PC: 右端に absolute 配置） */}
          <Box sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1,
            position: { xs: 'static', sm: 'absolute' },
            right: { sm: 0 },
          }}>
            <Tooltip title={compactMode ? '全件表示に戻す' : 'コンパクト表示'}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <IconButton
                  size="small"
                  onClick={() => {
                    const next = !compactMode;
                    setCompactMode(next);
                    document.cookie = `daylist_compact=${next}; path=/; max-age=${60 * 60 * 24 * 365}`;
                  }}
                  sx={{
                    bgcolor: compactMode ? '#475569' : 'rgba(0,0,0,0.06)',
                    color: compactMode ? '#fff' : '#64748b',
                    '&:hover': { bgcolor: compactMode ? '#334155' : 'rgba(0,0,0,0.12)' },
                    width: 32, height: 32, fontSize: 14,
                  }}
                >
                  {compactMode ? '⊟' : '⊞'}
                </IconButton>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9, mt: 0.3 }}>
                  {compactMode ? 'compact' : ''}
                </Typography>
              </Box>
            </Tooltip>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <IconButton
                color="primary"
                onClick={handleManualRefresh}
                size="small"
                sx={{
                  bgcolor: 'primary.main', color: '#fff',
                  '&:hover': { bgcolor: 'primary.dark' },
                  width: 32, height: 32,
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, mt: 0.3 }}>
                {lastUpdated}
              </Typography>
            </Box>
          </Box>
        </Box>
        {/* スマホ時: タブも sticky ブロック内 */}
        {isMobile && !loading && (
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              minHeight: 40,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Tab label="貸出" sx={{ flex: 2, minWidth: 0, fontSize: 13, fontWeight: 700, px: 0, minHeight: 40, py: 0.5 }} />
            <Tab label="返却" sx={{ flex: 2, minWidth: 0, fontSize: 13, fontWeight: 700, px: 0, minHeight: 40, py: 0.5 }} />
            <Tab icon={<SearchIcon sx={{ fontSize: 18 }} />} sx={{ flex: 1, minWidth: 0, px: 0, minHeight: 40, py: 0.5 }} />
            <Tab icon={<SettingsIcon sx={{ fontSize: 18 }} />} sx={{ flex: 1, minWidth: 0, px: 0, minHeight: 40, py: 0.5 }} />
          </Tabs>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {isMobile ? (
            <Box>
              <Box role="tabpanel" hidden={tabValue !== 0}>
                {tabValue === 0 && (
                  <ReservationTable
                    key={`dep-${settingsVersion}`}
                    hideToolbar
                    compactMode={compactMode}
                    isSimpleView={isMobile}
                    isToday={selectedDate === dayjs().format('YYYY-MM-DD')}
                    title="貸出"
                    data={finalDepartures}
                    type="departure"
                    vehicles={vehicles}
                    formatTime={formatTime}
                    onVehicleClick={handleVehicleClick}
                    onStatusClick={handleStatusClick}
                    onLocationStatusClick={handleLocationStatusClick}
                    onReservationSave={handleReservationSave}
                    onQuickUpdate={handleQuickUpdate}
                    onStaffClick={handleStaffClick}
                    onContactClick={(r: any) => setContactModalReservation(r)}
                    onLicenseToggle={handleLicenseToggle}
                    onCommunicationToolsUpdate={handleCommunicationToolsUpdate}
                    squarePaymentCache={squarePaymentCache}
                    squarePaymentLoading={squarePaymentLoading}
                    onPaymentCheck={handlePaymentCheck}
                    onBeforeOpenReservation={handleBeforeOpenReservation}
                    forceCloseReservationPanel={!!selectedVehicle}
                    reservationStatusList={reservationStatusList}
                    vehicleStatusList={vehicleStatusList}
                    upcomingStatuses={upcomingStatuses}
                    onCheckUpdate={handleCheckUpdate}
                    onAssignClick={handleOpenAssignModal}
                    carTypesList={carTypesList}
                    onHoverVehicle={setHoveredVehicleId}
                    onLeaveVehicle={() => setHoveredVehicleId(null)}
                    lineUsers={lineUsers}
                    onOpenLineChat={handleOpenLineChat}
                    onVehicleSwap={handleVehicleSwapRequest}
                    vehicleDragSource={vehicleDragSource}
                    onVehicleDragSourceChange={setVehicleDragSource}
                    staffList={staffList}
                    locationStatusList={locationStatusList}
                    externalSearchTerm=""
                    onSettingsOpen={() => {}}
                  />
                )}
              </Box>
              <Box role="tabpanel" hidden={tabValue !== 1}>
                {tabValue === 1 && (
                  <ReservationTable
                    key={`ret-${settingsVersion}`}
                    hideToolbar
                    compactMode={compactMode}
                    isSimpleView={isMobile}
                    isToday={selectedDate === dayjs().format('YYYY-MM-DD')}
                    title="返却"
                    data={finalReturns}
                    type="return"
                    vehicles={vehicles}
                    formatTime={formatTime}
                    onVehicleClick={handleVehicleClick}
                    onStatusClick={handleStatusClick}
                    onLocationStatusClick={handleLocationStatusClick}
                    onReservationSave={handleReservationSave}
                    onQuickUpdate={handleQuickUpdate}
                    onStaffClick={handleStaffClick}
                    onContactClick={(r: any) => setContactModalReservation(r)}
                    onLicenseToggle={handleLicenseToggle}
                    onCommunicationToolsUpdate={handleCommunicationToolsUpdate}
                    squarePaymentCache={squarePaymentCache}
                    squarePaymentLoading={squarePaymentLoading}
                    onPaymentCheck={handlePaymentCheck}
                    onBeforeOpenReservation={handleBeforeOpenReservation}
                    forceCloseReservationPanel={!!selectedVehicle}
                    reservationStatusList={reservationStatusList}
                    vehicleStatusList={vehicleStatusList}
                    upcomingStatuses={upcomingStatuses}
                    onCheckUpdate={handleCheckUpdate}
                    onAssignClick={handleOpenAssignModal}
                    carTypesList={carTypesList}
                    onHoverVehicle={setHoveredVehicleId}
                    onLeaveVehicle={() => setHoveredVehicleId(null)}
                    lineUsers={lineUsers}
                    onOpenLineChat={handleOpenLineChat}
                    onVehicleSwap={handleVehicleSwapRequest}
                    vehicleDragSource={vehicleDragSource}
                    onVehicleDragSourceChange={setVehicleDragSource}
                    staffList={staffList}
                    locationStatusList={locationStatusList}
                    externalSearchTerm=""
                    onSettingsOpen={() => {}}
                  />
                )}
              </Box>
              {/* 検索タブ */}
              <Box role="tabpanel" hidden={tabValue !== 2}>
                {tabValue === 2 && (
                  <Box sx={{ p: 2, bgcolor: '#fff', position: 'relative', zIndex: 1, minHeight: '100vh' }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="顧客名・車両コード・予約コードで検索"
                      value={mobileSearchTerm}
                      onChange={(e) => setMobileSearchTerm(e.target.value)}
                      autoFocus
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon color="action" fontSize="small" /></InputAdornment>,
                        endAdornment: mobileSearchTerm ? <InputAdornment position="end"><IconButton size="small" onClick={() => setMobileSearchTerm('')}><CloseIcon fontSize="small" /></IconButton></InputAdornment> : null,
                      }}
                      sx={{ mb: 2 }}
                    />
                    {mobileSearchTerm.trim() ? (
                      <>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>貸出 ({finalDepartures.filter(r => {
                          const q = mobileSearchTerm.toLowerCase();
                          return (r.customer_name || '').toLowerCase().includes(q) || (r.vehicle?.vehicle_code || '').toLowerCase().includes(q) || (r.contact_code || '').toLowerCase().includes(q);
                        }).length})</Typography>
                        <ReservationTable
                          key={`search-dep-${settingsVersion}`}
                          hideToolbar
                          compactMode
                          isSimpleView={isMobile}
                          isToday={selectedDate === dayjs().format('YYYY-MM-DD')}
                          title="貸出"
                          data={finalDepartures}
                          type="departure"
                          vehicles={vehicles}
                          formatTime={formatTime}
                          onVehicleClick={handleVehicleClick}
                          onStatusClick={handleStatusClick}
                          onLocationStatusClick={handleLocationStatusClick}
                          onReservationSave={handleReservationSave}
                          onQuickUpdate={handleQuickUpdate}
                          onStaffClick={handleStaffClick}
                    onContactClick={(r: any) => setContactModalReservation(r)}
                          onLicenseToggle={handleLicenseToggle}
                          onCommunicationToolsUpdate={handleCommunicationToolsUpdate}
                          squarePaymentCache={squarePaymentCache}
                          squarePaymentLoading={squarePaymentLoading}
                          onPaymentCheck={handlePaymentCheck}
                          onBeforeOpenReservation={handleBeforeOpenReservation}
                          forceCloseReservationPanel={!!selectedVehicle}
                          reservationStatusList={reservationStatusList}
                          vehicleStatusList={vehicleStatusList}
                          upcomingStatuses={upcomingStatuses}
                          onCheckUpdate={handleCheckUpdate}
                          onAssignClick={handleOpenAssignModal}
                          carTypesList={carTypesList}
                          lineUsers={lineUsers}
                          onOpenLineChat={handleOpenLineChat}
                    onVehicleSwap={handleVehicleSwapRequest}
                    vehicleDragSource={vehicleDragSource}
                    onVehicleDragSourceChange={setVehicleDragSource}
                          staffList={staffList}
                          locationStatusList={locationStatusList}
                          externalSearchTerm={mobileSearchTerm}
                          onHoverVehicle={setHoveredVehicleId}
                          onLeaveVehicle={() => setHoveredVehicleId(null)}
                          onSettingsOpen={() => {}}
                        />
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 700 }}>返却 ({finalReturns.filter(r => {
                          const q = mobileSearchTerm.toLowerCase();
                          return (r.customer_name || '').toLowerCase().includes(q) || (r.vehicle?.vehicle_code || '').toLowerCase().includes(q) || (r.contact_code || '').toLowerCase().includes(q);
                        }).length})</Typography>
                        <ReservationTable
                          key={`search-ret-${settingsVersion}`}
                          hideToolbar
                          compactMode
                          isSimpleView={isMobile}
                          isToday={selectedDate === dayjs().format('YYYY-MM-DD')}
                          title="返却"
                          data={finalReturns}
                          type="return"
                          vehicles={vehicles}
                          formatTime={formatTime}
                          onVehicleClick={handleVehicleClick}
                          onStatusClick={handleStatusClick}
                          onLocationStatusClick={handleLocationStatusClick}
                          onReservationSave={handleReservationSave}
                          onQuickUpdate={handleQuickUpdate}
                          onStaffClick={handleStaffClick}
                    onContactClick={(r: any) => setContactModalReservation(r)}
                          onLicenseToggle={handleLicenseToggle}
                          onCommunicationToolsUpdate={handleCommunicationToolsUpdate}
                          squarePaymentCache={squarePaymentCache}
                          squarePaymentLoading={squarePaymentLoading}
                          onPaymentCheck={handlePaymentCheck}
                          onBeforeOpenReservation={handleBeforeOpenReservation}
                          forceCloseReservationPanel={!!selectedVehicle}
                          reservationStatusList={reservationStatusList}
                          vehicleStatusList={vehicleStatusList}
                          upcomingStatuses={upcomingStatuses}
                          onCheckUpdate={handleCheckUpdate}
                          onAssignClick={handleOpenAssignModal}
                          carTypesList={carTypesList}
                          lineUsers={lineUsers}
                          onOpenLineChat={handleOpenLineChat}
                    onVehicleSwap={handleVehicleSwapRequest}
                    vehicleDragSource={vehicleDragSource}
                    onVehicleDragSourceChange={setVehicleDragSource}
                          staffList={staffList}
                          locationStatusList={locationStatusList}
                          externalSearchTerm={mobileSearchTerm}
                          onHoverVehicle={setHoveredVehicleId}
                          onLeaveVehicle={() => setHoveredVehicleId(null)}
                          onSettingsOpen={() => {}}
                        />
                      </>
                    ) : (
                      <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                        検索語を入力してください
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
              {/* 設定タブ */}
              <Box role="tabpanel" hidden={tabValue !== 3}>
                {tabValue === 3 && (
                  <Box sx={{ p: 2, bgcolor: '#fff', position: 'relative', zIndex: 1, minHeight: '100vh' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>表示設定</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2">コンパクト表示</Typography>
                        <Box
                          onClick={() => {
                            const next = !compactMode;
                            setCompactMode(next);
                            document.cookie = `daylist_compact=${next}; path=/; max-age=${60 * 60 * 24 * 365}`;
                          }}
                          sx={{ width: 44, height: 24, borderRadius: 12, cursor: 'pointer', position: 'relative', bgcolor: compactMode ? '#3b82f6' : '#94a3b8', transition: 'background 0.2s' }}
                        >
                          <Box sx={{ position: 'absolute', top: 2, left: compactMode ? 22 : 2, width: 20, height: 20, borderRadius: '50%', bgcolor: '#fff', transition: 'left 0.2s' }} />
                        </Box>
                      </Box>
                    </Box>

                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1, color: '#475569' }}>表示項目</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {MOBILE_FIELD_OPTIONS.map(field => (
                        <Box
                          key={field.id}
                          onClick={() => handleMobileFieldToggle(field.id)}
                          sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            py: 1, px: 1, borderRadius: 1, cursor: 'pointer',
                            '&:hover': { bgcolor: '#f1f5f9' },
                          }}
                        >
                          <Typography variant="body2">{field.label}</Typography>
                          <Box sx={{
                            width: 44, height: 24, borderRadius: 12, position: 'relative',
                            bgcolor: mobileFieldVisibility[field.id] ? '#3b82f6' : '#94a3b8',
                            transition: 'background 0.2s',
                          }}>
                            <Box sx={{
                              position: 'absolute', top: 2,
                              left: mobileFieldVisibility[field.id] ? 22 : 2,
                              width: 20, height: 20, borderRadius: '50%', bgcolor: '#fff',
                              transition: 'left 0.2s',
                            }} />
                          </Box>
                        </Box>
                      ))}
                    </Box>

                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1, color: '#475569' }}>予約ステータス</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {(reservationStatusList || []).map((status: any) => {
                        const isOn = mobileStatusFilter[String(status.id)] !== false;
                        return (
                          <Box
                            key={status.id}
                            onClick={() => handleMobileStatusToggle(status.id)}
                            sx={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              py: 1, px: 1, borderRadius: 1, cursor: 'pointer',
                              '&:hover': { bgcolor: '#f1f5f9' },
                            }}
                          >
                            <Typography variant="body2">{status.label}</Typography>
                            <Box sx={{
                              width: 44, height: 24, borderRadius: 12, position: 'relative',
                              bgcolor: isOn ? '#3b82f6' : '#94a3b8',
                              transition: 'background 0.2s',
                            }}>
                              <Box sx={{
                                position: 'absolute', top: 2,
                                left: isOn ? 22 : 2,
                                width: 20, height: 20, borderRadius: '50%', bgcolor: '#fff',
                                transition: 'left 0.2s',
                              }} />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2
            }}>
              <Box sx={{ minWidth: 0 }}>
                <ReservationTable
                  compactMode={compactMode}
                  isSimpleView={isMobile}
                  isToday={selectedDate === dayjs().format('YYYY-MM-DD')}
                  title="貸出"
                  data={finalDepartures}
                  type="departure"
                  vehicles={vehicles}
                  formatTime={formatTime}
                  onVehicleClick={handleVehicleClick}
                  onStatusClick={handleStatusClick}
                  onLocationStatusClick={handleLocationStatusClick}
                  onReservationSave={handleReservationSave}
                  onQuickUpdate={handleQuickUpdate}
                  onStaffClick={handleStaffClick}
                    onContactClick={(r: any) => setContactModalReservation(r)}
                  onLicenseToggle={handleLicenseToggle}
                  onCommunicationToolsUpdate={handleCommunicationToolsUpdate}
                  squarePaymentCache={squarePaymentCache}
                  squarePaymentLoading={squarePaymentLoading}
                  onPaymentCheck={handlePaymentCheck}
                  onBeforeOpenReservation={handleBeforeOpenReservation}
                  forceCloseReservationPanel={!!selectedVehicle}
                  reservationStatusList={reservationStatusList}
                  vehicleStatusList={vehicleStatusList}
                  upcomingStatuses={upcomingStatuses}
                  locationStatusList={locationStatusList}
                  staffList={staffList}
                  onCheckUpdate={handleCheckUpdate}
                  onAssignClick={handleOpenAssignModal}
                  carTypesList={carTypesList}
                  allowCopy={false}
                  onHoverVehicle={setHoveredVehicleId}
                  onLeaveVehicle={() => setHoveredVehicleId(null)}
                  lineUsers={lineUsers}
                  onOpenLineChat={handleOpenLineChat}
                  onVehicleSwap={handleVehicleSwapRequest}
                  vehicleDragSource={vehicleDragSource}
                  onVehicleDragSourceChange={setVehicleDragSource}
                  externalSearchTerm={undefined}
                  onSettingsOpen={() => {}}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <ReservationTable
                  compactMode={compactMode}
                  isSimpleView={isMobile}
                  isToday={selectedDate === dayjs().format('YYYY-MM-DD')}
                  title="返却"
                  data={finalReturns}
                  type="return"
                  vehicles={vehicles}
                  formatTime={formatTime}
                  onVehicleClick={handleVehicleClick}
                  onStatusClick={handleStatusClick}
                  onLocationStatusClick={handleLocationStatusClick}
                  onReservationSave={handleReservationSave}
                  onQuickUpdate={handleQuickUpdate}
                  onStaffClick={handleStaffClick}
                    onContactClick={(r: any) => setContactModalReservation(r)}
                  onLicenseToggle={handleLicenseToggle}
                  onCommunicationToolsUpdate={handleCommunicationToolsUpdate}
                  squarePaymentCache={squarePaymentCache}
                  squarePaymentLoading={squarePaymentLoading}
                  onPaymentCheck={handlePaymentCheck}
                  onBeforeOpenReservation={handleBeforeOpenReservation}
                  forceCloseReservationPanel={!!selectedVehicle}
                  reservationStatusList={reservationStatusList}
                  vehicleStatusList={vehicleStatusList}
                  upcomingStatuses={upcomingStatuses}
                  locationStatusList={locationStatusList}
                  staffList={staffList}
                  onCheckUpdate={handleCheckUpdate}
                  onAssignClick={handleOpenAssignModal}
                  carTypesList={carTypesList}
                  allowCopy={false}
                  onHoverVehicle={setHoveredVehicleId}
                  onLeaveVehicle={() => setHoveredVehicleId(null)}
                  lineUsers={lineUsers}
                  onOpenLineChat={handleOpenLineChat}
                  onVehicleSwap={handleVehicleSwapRequest}
                  vehicleDragSource={vehicleDragSource}
                  onVehicleDragSourceChange={setVehicleDragSource}
                  externalSearchTerm={undefined}
                  onSettingsOpen={() => {}}
                />
              </Box>
            </Box>
          )}

          {/* コンパクト表示トグル */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                const next = !compactMode;
                setCompactMode(next);
                document.cookie = `daylist_compact=${next}; path=/; max-age=${60 * 60 * 24 * 365}`;
              }}
              sx={{ fontSize: 11, px: 2, py: 0.5, borderRadius: 4, color: 'text.secondary', bgcolor: 'rgba(0,0,0,0.04)', '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' } }}
            >
              {compactMode ? '全件表示に戻す' : 'コンパクト表示'}
            </Button>
          </Box>

          {/* 車両ポジションマップ */}
          <Box sx={{ mt: 8, mb: 4, pt: 4, borderTop: '1px solid #eee' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              車両ポジション
            </Typography>
            <VehiclePositionMap
              vehicles={vehicles}
              vehicleStatusList={vehicleStatusList}
              locationStatusList={locationStatusList}
              departures={finalDepartures}
              returns={finalReturns}
              upcomingStatuses={upcomingStatuses}
              onVehicleClick={(v) => handleVehicleClick(v, null)}
              onStaffClick={handleStaffClick}
              onReservationClick={(r) => { window.open(`/reservations?id=${r.id}`, '_blank'); }}
              onOpenLineChat={handleOpenLineChat}
              lineUsers={lineUsers}
              staffList={staffList}
              userId={user?.id}
            />
          </Box>

          {/* カスタムロケーション セクション */}
          <Box sx={{ mt: 4, mb: 4, pt: 4, borderTop: '1px solid #eee' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              車両位置
            </Typography>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Tabs
                value={bottomTabValue}
                onChange={handleBottomTabChange}
                aria-label="location tabs"
                textColor="primary"
                indicatorColor="primary"
                sx={{ flexGrow: 1 }}
              >
                {bottomTabConfigs.map((config, index) => (
                  <Tab
                    key={index}
                    label={config.name || `タブ${index + 1}`}
                    sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                  />
                ))}
              </Tabs>
              <Tooltip title="タブの絞り込み設定">
                <IconButton onClick={handleOpenTabConfig} color="primary" sx={{ ml: 2 }}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {locationGroupVehicles.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', px: 2 }}>
                現在、{bottomTabConfigs[bottomTabValue]?.name || '対象'}に配置されている車両はありません。
              </Typography>
            ) : (
              <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#fff' }}>
                {locationGroupVehicles.map((v, idx) => {
                  const upcoming = upcomingStatuses[v.id];
                  const nextRes = upcoming?.next;
                  return (
                    <Box
                      key={v.id}
                      onClick={() => handleVehicleClick(v, null)}
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: idx === locationGroupVehicles.length - 1 ? 'none' : '1px solid #eee',
                        transition: 'background-color 0.2s',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#f8fafc'
                        }
                      }}
                    >
                      <Typography sx={{ fontWeight: 'bold', color: '#334155' }}>
                        {v.vehicle_code}
                      </Typography>
                      <Box sx={{ textAlign: 'right' }}>
                        {nextRes ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              次回予約:
                            </Typography>
                            <Typography variant="caption" sx={{
                              fontWeight: 'bold',
                              color: upcoming.color
                            }}>
                              {dayjs(nextRes.start_date).format('M/D HH:mm')}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            次回予約なし
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Paper>
            )}
          </Box>
        </>
      )}

      <VehicleDetailPanel
        open={Boolean(selectedVehicle)}
        vehicle={selectedVehicle}
        onClose={handleClosePanel}
        onSave={handleSaveVehicle}
        onDirtyChange={setVehiclePanelDirty}
        vehicleStatusList={vehicleStatusList}
        locationStatusList={locationStatusList}
        onAssignClick={selectedVehicleReservation ? () => handleOpenAssignModal(selectedVehicleReservation) : null}
      />

      {/* タブ設定ダイアログ */}
      <Dialog open={tabConfigDialogOpen} onClose={() => setTabConfigDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>下部タブの絞り込み設定</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3, fontSize: '0.85rem' }}>
            各タブの名称と、表示したいステータスを複数選択できます。
            選択したステータスのいずれかが車両の状態や本日の予約に該当する場合、その車両が表示されます。
          </DialogContentText>
          <Stack spacing={4}>
            {editingTabConfigs.map((config, index) => {
              return (
                <Box key={index} sx={{ p: 2, border: '1px solid #eee', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                    タブ {index + 1} の設定
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="タブ名"
                      fullWidth
                      size="small"
                      value={config.name || ''}
                      onChange={(e) => handleTabConfigChange(index, 'name', e.target.value)}
                      placeholder="例: 空港店"
                    />
                    <FormControl fullWidth size="small">
                      <InputLabel id={`tab-select-label-${index}`}>表示するステータス（複数可）</InputLabel>
                      <Select
                        labelId={`tab-select-label-${index}`}
                        multiple
                        value={config.selectedLabels || []}
                        onChange={(e) => handleTabConfigChange(index, 'selectedLabels', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                        input={<OutlinedInput label="表示するステータス（複数可）" />}
                        renderValue={(selected) => Array.isArray(selected) ? selected.join(', ') : ''}
                      >
                        {[
                          { category: "車両", items: vehicleStatusList },
                          { category: "移動", items: locationStatusList.filter(o => o.id !== null) },
                          { category: "予約", items: reservationStatusList }
                        ].map((cat) => [
                          <MenuItem key={`header-${cat.category}`} disabled sx={{ fontWeight: "bold", opacity: "1 !important", color: "text.secondary", fontSize: "0.75rem" }}>
                            {cat.category}
                          </MenuItem>,
                          ...cat.items.map((opt) => (
                            <MenuItem key={`${cat.category}-${opt.id}`} value={opt.label}>
                              <Checkbox checked={(config.selectedLabels || []).indexOf(opt.label) > -1} size="small" />
                              <ListItemText primary={opt.label} />
                            </MenuItem>
                          ))
                        ])}
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.includeArchived === true}
                          onChange={(e) => handleTabConfigChange(index, 'includeArchived', e.target.checked)}
                          size="small"
                          color="warning"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                          アーカイブ済みの車両も含める（active=OFF / 使用停止）
                        </Typography>
                      }
                    />
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setTabConfigDialogOpen(false)} color="inherit" size="small">キャンセル</Button>
          <Button onClick={handleSaveTabConfig} variant="contained" color="primary" size="small">保存</Button>
        </DialogActions>
      </Dialog>

      {/* 未保存確認ダイアログ */}
      {unsavedConfirm && (
        <div>
          <div
            onClick={() => handleUnsavedConfirmAction('cancel')}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.4)',
              zIndex: 3900
            }}
          />
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 4000,
              background: '#fff',
              padding: 16,
              borderRadius: 8,
              border: '1px solid #ccc',
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
              minWidth: 360,
              maxWidth: 440
            }}
          >
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                未保存の情報がありますが{(unsavedConfirm.targetVehicle || unsavedConfirm.closeVehiclePanel) ? '新しいパネルを開きますか？' : '閉じますか？'}
              </div>
              <div style={{ fontSize: 14, color: '#64748b' }}>
                未保存の変更があります。
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="outlined" onClick={() => handleUnsavedConfirmAction('cancel')}>
                いいえ
              </Button>
              <Button variant="contained" onClick={() => handleUnsavedConfirmAction('discard')}>
                破棄して{(unsavedConfirm.targetVehicle || unsavedConfirm.closeVehiclePanel) ? '開く' : '閉じる'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={statusModal.open} onClose={() => setStatusModal({ ...statusModal, open: false })}>
        <DialogTitle>ステータス変更</DialogTitle>
        <DialogContent>
          <Grid {...{container: true} as any} spacing={1} sx={{ mt: 1 }}>
            {vehicleStatusList.map((status) => (
              <Grid {...{item: true, xs: 4} as any} key={status.id}>
                <Button
                  variant="contained"
                  color={(status.color || 'primary') as any}
                  fullWidth
                  onClick={() => handleStatusUpdate(status.id)}
                  sx={{
                    height: '100%',
                    ...(status.bg_color && {
                      backgroundColor: status.bg_color,
                      '&:hover': {
                        backgroundColor: status.bg_color,
                        filter: 'brightness(0.9)',
                      }
                    }),
                    ...(status.text_color && {
                      color: status.text_color
                    })
                  }}
                >
                  {status.label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>

      <Dialog open={locationStatusModal.open} onClose={() => setLocationStatusModal({ ...locationStatusModal, open: false })}>
        <DialogTitle>移動ステータス変更</DialogTitle>
        <DialogContent>
          <Grid {...{container: true} as any} spacing={1} sx={{ mt: 1 }}>
            {getLocationStatusOptions(locationStatusModal.listType).map((status) => (
              <Grid {...{item: true, xs: 4} as any} key={status.id ?? 'null'}>
                <Button
                  variant={status.bg_color ? "contained" : "outlined"}
                  fullWidth
                  onClick={() => handleLocationStatusUpdate(status.id)}
                  sx={{
                    height: '100%',
                    fontSize: '0.75rem',
                    minHeight: '48px',
                    ...(status.bg_color && {
                      backgroundColor: status.bg_color,
                      color: status.text_color || '#fff',
                      borderColor: status.bg_color,
                      '&:hover': {
                        backgroundColor: `${status.bg_color}dd`,
                        filter: 'brightness(0.9)',
                      }
                    }),
                    ...(!status.bg_color && {
                      color: status.text_color || undefined,
                      borderColor: status.text_color || undefined
                    })
                  }}
                >
                  {status.label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>

      {/* 連絡モーダル（到着連絡 + ピックアップサブゾーン選択） */}
      <Dialog open={!!contactModalReservation} onClose={() => setContactModalReservation(null)} maxWidth="xs" fullWidth>
        {contactModalReservation && (() => {
          const r = contactModalReservation;
          const jd = r.json_data || r.detail?.json_data;
          const hasArrival = jd?.data?.['到着連絡'] === '有り';
          const currentAirport = jd?.data?.['空港'];
          return (
            <>
              <DialogTitle sx={{ pb: 1, fontSize: '1rem' }}>{r.customer_name}</DialogTitle>
              <DialogContent sx={{ pb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {dayjs(r.start_date).format('HH:mm')} 貸出
                </Typography>
                <Button
                  variant={hasArrival ? 'contained' : 'outlined'}
                  color={hasArrival ? 'success' : 'inherit'}
                  fullWidth
                  onClick={async () => {
                    try {
                      const currentJsonData = jd || { data: {} };
                      const newData = { ...(currentJsonData.data || {}) };
                      if (hasArrival) {
                        delete newData['到着連絡'];
                        delete newData['空港'];
                      } else {
                        newData['到着連絡'] = '有り';
                        if (!newData['空港']) newData['空港'] = '未';
                      }
                      await saveDispatchReservation({
                        ...r,
                        json_data: { ...currentJsonData, data: newData },
                        detail: { ...(r.detail || {}), json_data: { ...currentJsonData, data: newData } },
                      });
                      setToast({ open: true, message: hasArrival ? '到着連絡を取消' : '到着連絡を記録', severity: 'success' });
                      setContactModalReservation(null);
                    } catch {
                      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
                    }
                  }}
                  sx={{ mb: 1 }}
                >
                  {hasArrival ? '到着連絡: ON（クリックで取消）' : '到着連絡'}
                </Button>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {PICKUP_SUB_ZONES.filter(sz => sz.airportValue !== '未').map(sz => {
                      const isActive = currentAirport === sz.airportValue;
                      return (
                        <Button
                          key={sz.id}
                          variant={isActive ? 'contained' : 'outlined'}
                          size="small"
                          onClick={async () => {
                            try {
                              const currentJsonData = jd || { data: {} };
                              const newData = { ...(currentJsonData.data || {}), '到着連絡': '有り', '空港': isActive ? '未' : sz.airportValue };
                              await saveDispatchReservation({
                                ...r,
                                json_data: { ...currentJsonData, data: newData },
                                detail: { ...(r.detail || {}), json_data: { ...currentJsonData, data: newData } },
                              });
                              setToast({ open: true, message: `${r.customer_name} → ${sz.label}`, severity: 'success' });
                              setContactModalReservation(null);
                            } catch {
                              setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
                            }
                          }}
                          sx={{
                            fontSize: '0.75rem',
                            ...(isActive
                              ? { backgroundColor: sz.borderColor, color: (sz as any).textColor || '#fff', '&:hover': { backgroundColor: sz.borderColor, filter: 'brightness(0.9)' } }
                              : { borderColor: sz.borderColor, color: sz.borderColor }
                            ),
                          }}
                        >
                          {sz.label}
                        </Button>
                      );
                    })}
                    {hasArrival && (
                      <Button
                        variant="outlined"
                        size="small"
                        color="inherit"
                        onClick={async () => {
                          try {
                            const currentJsonData = jd || { data: {} };
                            const newData = { ...(currentJsonData.data || {}) };
                            delete newData['到着連絡'];
                            delete newData['空港'];
                            await saveDispatchReservation({
                              ...r,
                              json_data: { ...currentJsonData, data: newData },
                              detail: { ...(r.detail || {}), json_data: { ...currentJsonData, data: newData } },
                            });
                            setToast({ open: true, message: `${r.customer_name} → クリア`, severity: 'success' });
                            setContactModalReservation(null);
                          } catch {
                            setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
                          }
                        }}
                        sx={{ fontSize: '0.75rem' }}
                      >
                        クリア
                      </Button>
                    )}
                  </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setContactModalReservation(null)}>閉じる</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* スタッフ選択モーダル */}
      <Dialog
        open={staffModal.open}
        onClose={() => setStaffModal({ ...staffModal, open: false })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {staffModal.staffType === 'driver' ? '運転者' : '対応者'}を選択
          {staffModal.reservation && (
            <Typography variant="body2" color="text.secondary">
              {staffModal.reservation.customer_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid {...{container: true} as any} spacing={1}>
            {/* 未指定オプション */}
            <Grid {...{item: true, xs: 4} as any}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleStaffSelect(null)}
                sx={{
                  height: '100%',
                  borderColor: '#bdbdbd',
                  color: '#757575',
                  '&:hover': { backgroundColor: '#f5f5f5' }
                }}
              >
                未指定
              </Button>
            </Grid>
            {/* スタッフリスト */}
            {getFilteredStaffList(getCurrentStaffId()).map((staff) => {
              const isCurrentStaff = staff.id === getCurrentStaffId();
              return (
                <Grid {...{item: true, xs: 4} as any} key={staff.id}>
                  <Button
                    fullWidth
                    variant={staff.bg_color ? "contained" : "outlined"}
                    onClick={() => handleStaffSelect(staff)}
                    sx={{
                      height: '100%',
                      backgroundColor: staff.bg_color || undefined,
                      color: staff.text_color || undefined,
                      borderColor: staff.bg_color ? 'transparent' : (staff.text_color || undefined),
                      boxShadow: isCurrentStaff ? `0 0 0 2px #1976d2` : undefined,
                      '&:hover': {
                        backgroundColor: staff.bg_color ? `${staff.bg_color}dd` : undefined,
                        filter: 'brightness(0.9)',
                      }
                    }}
                  >
                    {staff.staff_name}
                  </Button>
                </Grid>
              );
            })}
          </Grid>
          {/* ピン留めスタッフセクション（運転者のみ） */}
          {staffModal.staffType === 'driver' && pinnedStaffUsers.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, mb: 0.5, display: 'block' }}>
                スポットスタッフ
              </Typography>
              <Grid {...{container: true} as any} spacing={1}>
                {pinnedStaffUsers.map((pStaff) => (
                  <Grid {...{item: true, xs: 4} as any} key={pStaff.line_user_id}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => handleStaffSelect({ ...pStaff, _isPinnedStaff: true })}
                      sx={{
                        height: '100%',
                        backgroundColor: '#e8f5e9',
                        borderColor: '#06C755',
                        color: '#2e7d32',
                        '&:hover': {
                          backgroundColor: '#c8e6c9',
                        }
                      }}
                    >
                      {pStaff.staffName}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStaffModal({ ...staffModal, open: false })}>
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialog.open}
        onClose={() => handleConfirmClose(false)}
      >
        <DialogTitle>確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            チェックを初期化しますか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleConfirmClose(false)} color="primary">
            いいえ (そのまま)
          </Button>
          <Button onClick={() => handleConfirmClose(true)} color="primary" autoFocus>
            はい (初期化)
          </Button>
        </DialogActions>
      </Dialog>

      {/* 車両アサインモーダル */}
      <VehicleAssignModal
        open={assignModalOpen}
        onClose={handleCloseAssignModal}
        reservation={assignTargetReservation}
        vehicleList={Object.values(vehicles)}
        vehicles={vehicles}
        carTypesList={carTypesList}
        onAssign={executeAssignment}
        onUpdateVehicleStatus={handleUpdateVehicleStatus}
        vehicleStatusList={vehicleStatusList}
        allReservations={[...departures, ...returns]}
      />


      {/* Square 支払確認モーダル */}
      <Dialog
        open={squareModal.open}
        onClose={() => setSquareModal({ open: false, reservation: null, paymentData: null, loading: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" component="span">支払確認</Typography>
            {squareModal.reservation && (
              <Typography variant="subtitle2" color="text.secondary">
                {squareModal.reservation.customer_name} ({squareModal.reservation.contact_code || squareModal.reservation.detail?.contact_id})
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* 支払状態の手動変更ボタン */}
            {(() => {
              const currentPayment = Number(squareModal.reservation?.detail?.payment ?? 0);
              const isPaid = currentPayment === 1;

              return (
                <Button
                  variant={isPaid ? 'outlined' : 'contained'}
                  color={isPaid ? 'warning' : 'success'}
                  size="small"
                  onClick={async () => {
                    const reservation = squareModal.reservation;
                    if (!reservation) return;

                    const newPaymentStatus = isPaid ? 0 : 1;

                    try {
                      // bug fix: reservation 全体を spread して car_type / vehicle_id を保持する
                      // （以前は id 等の必須フィールドだけ列挙し car_type が '' でクリアされる事象あり）
                      await saveDispatchReservation({
                        ...reservation,
                        customer_name: reservation.customer_name || '予約',
                        detail: {
                          ...(reservation.detail || {}),
                          payment: newPaymentStatus
                        }
                      });

                      // 変更履歴を記録
                      recordChange(
                        'reservation',
                        reservation.id,
                        // NOTE: Fallback required for spread. Detail is validated non-null by SyncService, but kept for runtime safety.
                        { ...reservation, detail: { ...(reservation.detail || {}), payment: isPaid } },
                        { ...reservation, detail: { ...(reservation.detail || {}), payment: !isPaid } },
                        'user'
                      );

                      // ローカルステートを更新
                      const updateRow = (r) => {
                        if (String(r.id) !== String(reservation.id)) return r;
                        return {
                          ...r,
                          // NOTE: Fallback required for spread. Detail is validated non-null by SyncService, but kept for runtime safety.
                          detail: { ...(r.detail || {}), payment: newPaymentStatus }
                        };
                      };
                      setDepartures(prev => prev.map(updateRow));
                      setReturns(prev => prev.map(updateRow));

                      // 予約オブジェクト自身も更新（モーダルの再描画用）
                      setSquareModal(prev => ({
                        ...prev,
                        reservation: {
                          ...prev.reservation,
                          detail: {
                            // NOTE: Fallback required for spread. Detail is validated non-null by SyncService, but kept for runtime safety.
                            ...(prev.reservation.detail || {}),
                            payment: newPaymentStatus
                          }
                        }
                      }));

                      setToast({
                        open: true,
                        message: newPaymentStatus === 1 ? '支払確認済みに更新しました' : '未払いに戻しました',
                        severity: 'success'
                      });

                      // モーダルは閉じない（続けて確認できるように）
                    } catch (error) {
                      console.error('Failed to update payment status:', error);
                      setToast({ open: true, message: '更新に失敗しました: ' + error.message, severity: 'error' });
                    }
                  }}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {isPaid ? '未払に戻す' : '確認済みにする'}
                </Button>
              );
            })()}
            <IconButton
              onClick={() => setSquareModal({ open: false, reservation: null, paymentData: null, loading: false })}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ minHeight: '150px' }}>
          {squareModal.loading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress size={30} sx={{ mb: 2 }} />
              <Typography color="text.secondary">Squareデータを読み込み中...</Typography>
            </Box>
          ) : !squareModal.paymentData || squareModal.paymentData.invoices?.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                該当するSquare請求データがありません。
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#7c3aed' }}>
                Square決済DB ({squareModal.paymentData.invoices?.length || 0}件)
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f3e8ff' }}>
                      <TableCell sx={{ fontWeight: 600 }}>請求番号</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>ステータス</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>金額</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>支払日</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>作成日</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>商品</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {squareModal.paymentData.invoices.map((inv: any, idx: number) => {
                      const order = squareModal.paymentData.orders?.find((o: any) => o.order_id === inv.order_id);
                      const lineItems = order?.line_items || [];
                      const payment = squareModal.paymentData.payments?.find((p: any) => p.order_id === inv.order_id);
                      const statusColor = { PAID: '#10b981', COMPLETED: '#10b981', CANCELED: '#ef4444', UNPAID: '#f59e0b', PARTIALLY_PAID: '#3b82f6' }[inv.status] || '#94a3b8';
                      return (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {inv.invoice_number || '-'}
                          </TableCell>
                          <TableCell>
                            <Chip label={inv.status} size="small" sx={{ bgcolor: statusColor, color: '#fff', fontSize: '0.7rem', fontWeight: 600 }} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            ¥{((inv.computed_amount || 0) / 1).toLocaleString()}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            {payment?.created_at ? dayjs(payment.created_at).format('MM/DD HH:mm') : inv.updated_at ? dayjs(inv.updated_at).format('MM/DD HH:mm') : '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            {inv.created_at ? dayjs(inv.created_at).format('YYYY/MM/DD') : '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', maxWidth: 200 }}>
                            {lineItems.length > 0
                              ? lineItems.map((li: any, i: number) => (
                                  <div key={i}>{li.name} x{li.quantity}</div>
                                ))
                              : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {squareModal.paymentData.refunds?.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  返金: {squareModal.paymentData.refunds.length}件
                  (¥{squareModal.paymentData.refunds.reduce((sum: number, r: any) => sum + Math.round((Number(r.amount) || 0) / 100), 0).toLocaleString()})
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSquareModal({ open: false, reservation: null, paymentData: null, loading: false })}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* LINE Chat Modal */}
      <Dialog
        open={lineChatModal.open}
        onClose={handleCloseLineChat}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            height: isMobile ? '100%' : '85vh',
            borderRadius: isMobile ? 0 : '16px',
            overflow: 'visible',
            position: 'relative'
          }
        }}
      >
        <IconButton
          onClick={handleCloseLineChat}
          sx={{
            position: 'absolute',
            right: isMobile ? 8 : -48,
            top: isMobile ? 8 : 0,
            zIndex: 2000,
            color: isMobile ? '#64748b' : 'white',
            bgcolor: isMobile ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.3)',
            '&:hover': {
              bgcolor: isMobile ? 'white' : 'rgba(0,0,0,0.5)',
              color: isMobile ? '#000' : 'white'
            },
            boxShadow: isMobile ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
          {lineChatModal.open && lineChatModal.lineUser && (
            <LineChatPanel
              user={{
                ...lineChatModal.lineUser,
                instance_reservation_id: lineChatModal.reservationId,
              }}
              onClose={handleCloseLineChat}
              onReload={fetchLineUsers}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* 車両スワップ確認Dialog */}
      <Dialog open={Boolean(vehicleSwapConfirm)} onClose={() => setVehicleSwapConfirm(null)}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '16px', pb: 1 }}>車両の入替確認</DialogTitle>
        <DialogContent>
          {vehicleSwapConfirm && (() => {
            const srcVehicle = Object.values(vehicles).find((v: any) => String(v.id) === String(vehicleSwapConfirm.source.vehicle_id)) as any;
            const tgtVehicle = Object.values(vehicles).find((v: any) => String(v.id) === String(vehicleSwapConfirm.target.vehicle_id)) as any;
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, background: '#f1f5f9', textAlign: 'center' }}>
                    <Typography variant="body2" fontWeight={700}>{vehicleSwapConfirm.source.customer_name}</Typography>
                    <Typography variant="h6" fontWeight={800} color="primary">{srcVehicle?.vehicle_code || '?'}</Typography>
                  </Box>
                  <Typography variant="h5" sx={{ color: '#94a3b8' }}>⇄</Typography>
                  <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, background: '#f1f5f9', textAlign: 'center' }}>
                    <Typography variant="body2" fontWeight={700}>{vehicleSwapConfirm.target.customer_name}</Typography>
                    <Typography variant="h6" fontWeight={800} color="primary">{tgtVehicle?.vehicle_code || '?'}</Typography>
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVehicleSwapConfirm(null)}>キャンセル</Button>
          <Button onClick={executeVehicleSwap} variant="contained" color="primary">入替する</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={6000} onClose={handleCloseToast}>
        <Alert onClose={handleCloseToast} severity={toast.severity as any} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
      {!isMobile && <TurnaroundLines hoveredVehicleId={hoveredVehicleId} />}
    </Box>
  );
}

// ターンアラウンド（同日返却・貸出）のドットを結ぶラインを描画するコンポーネント
const TurnaroundLines = ({ hoveredVehicleId }) => {
  const [lines, setLines] = React.useState([]);

  React.useEffect(() => {
    let requestRef;

    const updateLines = () => {
      if (requestRef) cancelAnimationFrame(requestRef);

      requestRef = requestAnimationFrame(() => {
        const dots = document.querySelectorAll('.turnaround-dot');
        const map = {};
        dots.forEach(dot => {
          const vid = dot.getAttribute('data-vehicle-id');
          const type = dot.getAttribute('data-type');
          if (!vid || !type) return;

          const dotRect = dot.getBoundingClientRect();

          // 所属するテーブルコンテナとそのヘッダー内の実際のセルを取得
          const container = dot.closest('.MuiTableContainer-root');
          // stickyHeaderの場合、thが固定の境界線となるためthを取得
          const headerCell = container ? container.querySelector('th') : null;

          let isVisible = true;
          let currentContainerRect = null;
          if (container && headerCell) {
            currentContainerRect = container.getBoundingClientRect();
            const headerRect = headerCell.getBoundingClientRect();

            // ヘッダーセルの下端より上（潜った）、またはコンテナの下端より下にある場合は非表示
            if (dotRect.top < headerRect.bottom || dotRect.bottom > currentContainerRect.bottom) {
              isVisible = false;
            }
          }

          if (!map[vid]) map[vid] = {};
          map[vid][type] = {
            rect: dotRect,
            visible: isVisible,
            containerRect: currentContainerRect
          };
        });

        const newLines = [];
        Object.keys(map).forEach(vid => {
          const dep = map[vid].departure;
          const ret = map[vid].return;
          // 両方のドットが画面内にあり、かつテーブルの可視範囲内にある場合のみ描画
          // かつ、その車両がホバーされている場合のみ
          if (dep && ret && dep.visible && ret.visible && String(vid) === String(hoveredVehicleId)) {
            newLines.push({
              id: vid,
              dep: dep.rect,
              ret: ret.rect,
              depContainerRect: dep.containerRect,
              retContainerRect: ret.containerRect
            });
          }
        });
        setLines(newLines);
      });
    };

    updateLines();
    window.addEventListener('scroll', updateLines, true);
    window.addEventListener('resize', updateLines);

    // アニメーションやロード後のレイアウト変更に対応するため一定間隔で更新
    const timer = setInterval(updateLines, 1000);

    return () => {
      if (requestRef) cancelAnimationFrame(requestRef);
      window.removeEventListener('scroll', updateLines, true);
      window.removeEventListener('resize', updateLines);
      clearInterval(timer);
    };
  }, [hoveredVehicleId]);

  return (
    <svg style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: 9999
    }}>
      {lines.map(line => {
        // 貸出側は横スクロールによるズレを防ぐため、常にテーブルコンテナの右端をX座標とする
        const depX = line.depContainerRect ? line.depContainerRect.right : line.dep.left + line.dep.width / 2;
        const depY = line.dep.top + line.dep.height / 2;
        const retX = line.ret.left + line.ret.width / 2;
        const retY = line.ret.top + line.ret.height / 2;

        return (
          <React.Fragment key={line.id}>
            {/* 接続ライン */}
            <line
              x1={depX}
              y1={depY}
              x2={retX}
              y2={retY}
              stroke="#d32f2f"
              strokeWidth="1.5"
              strokeOpacity="0.4"
              strokeDasharray="4 2"
            />
          </React.Fragment>
        );
      })}
    </svg>
  );
};
